import { useEffect, useState } from "react";
import { Accessibility, Minus, Plus, Contrast, Underline, RotateCcw, X } from "lucide-react";
import { cn } from "@/lib/utils";

const FONT_KEY = "fiaba-a11y-font";
const CONTRAST_KEY = "fiaba-a11y-contrast";
const LINKS_KEY = "fiaba-a11y-links";

export const AccessibilityToolbar = () => {
  const [open, setOpen] = useState(false);
  const [fontStep, setFontStep] = useState(0); // -1, 0, 1, 2
  const [highContrast, setHighContrast] = useState(false);
  const [underlineLinks, setUnderlineLinks] = useState(false);

  // Hydrate from storage
  useEffect(() => {
    const f = Number(localStorage.getItem(FONT_KEY) ?? "0");
    const c = localStorage.getItem(CONTRAST_KEY) === "1";
    const l = localStorage.getItem(LINKS_KEY) === "1";
    setFontStep(f);
    setHighContrast(c);
    setUnderlineLinks(l);
  }, []);

  // Apply
  useEffect(() => {
    const root = document.documentElement;
    const scale = 1 + fontStep * 0.1; // -10%, 0, +10%, +20%
    root.style.fontSize = `${100 * scale}%`;
    localStorage.setItem(FONT_KEY, String(fontStep));
  }, [fontStep]);

  useEffect(() => {
    document.documentElement.classList.toggle("a11y-contrast", highContrast);
    localStorage.setItem(CONTRAST_KEY, highContrast ? "1" : "0");
  }, [highContrast]);

  useEffect(() => {
    document.documentElement.classList.toggle("a11y-underline-links", underlineLinks);
    localStorage.setItem(LINKS_KEY, underlineLinks ? "1" : "0");
  }, [underlineLinks]);

  const reset = () => {
    setFontStep(0);
    setHighContrast(false);
    setUnderlineLinks(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Apri strumenti di accessibilità"
        aria-expanded={open}
        className="fixed left-3 bottom-3 z-50 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-elegant hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
      >
        <Accessibility size={22} aria-hidden="true" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Strumenti di accessibilità"
          className="fixed left-3 bottom-3 z-50 w-[280px] rounded-lg border border-border bg-card text-card-foreground shadow-elegant"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <p className="font-serif text-base font-bold text-primary">Accessibilità</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Chiudi pannello accessibilità"
              className="p-1 text-foreground/70 hover:text-foreground"
            >
              <X size={18} />
            </button>
          </div>

          <div className="space-y-3 p-4">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Dimensione testo
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFontStep((s) => Math.max(-1, s - 1))}
                  aria-label="Riduci dimensione testo"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-muted"
                >
                  <Minus size={16} />
                </button>
                <span className="flex-1 text-center text-sm font-medium" aria-live="polite">
                  {Math.round((1 + fontStep * 0.1) * 100)}%
                </span>
                <button
                  type="button"
                  onClick={() => setFontStep((s) => Math.min(2, s + 1))}
                  aria-label="Aumenta dimensione testo"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-muted"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setHighContrast((v) => !v)}
              aria-pressed={highContrast}
              className={cn(
                "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors",
                highContrast
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:bg-muted"
              )}
            >
              <Contrast size={16} aria-hidden="true" />
              Contrasto elevato
            </button>

            <button
              type="button"
              onClick={() => setUnderlineLinks((v) => !v)}
              aria-pressed={underlineLinks}
              className={cn(
                "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors",
                underlineLinks
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:bg-muted"
              )}
            >
              <Underline size={16} aria-hidden="true" />
              Sottolinea i link
            </button>

            <button
              type="button"
              onClick={reset}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-muted px-3 py-2 text-xs font-semibold text-foreground hover:bg-muted/80"
            >
              <RotateCcw size={14} aria-hidden="true" />
              Ripristina impostazioni
            </button>
          </div>
        </div>
      )}
    </>
  );
};
